import React, {
  createContext,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthService, getCurrentUser } from '@/core/auth/AuthService';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { useSyncStore } from '@/features/sync/stores/useSyncStore';
import { SyncProgress } from '@/features/sync/types/sync';
import syncRepository from '../repositories/SyncRepository';
import { SyncEngine } from '@/features/sync/services/SyncEngine';
import { syncError, syncLog } from '@/features/sync/utils/syncLogger';
import {
  invalidateCollectionQueries,
  invalidateLocalDataQueries,
  invalidatePageQueries,
} from '@/shared/query/queryKeys';

interface SyncContextType {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  handleSync: () => void;
  syncProgress: SyncProgress | null;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

interface SyncProviderProps {
  children: ReactNode;
  syncInterval?: number;
}

export const SyncProvider: React.FC<SyncProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  // Get state and actions from Zustand store
  const {
    user: currentUser,
    setSyncState,
    setUser,
    setAuthLoading,
    setLastSyncedAt,
    syncLoading,
  } = useSyncStore();

  // Add state for tracking sync progress
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);

  const setIsDirty = usePageStore(state => state.setIsDirty);

  // Services initialization
  const authService = useMemo(() => new AuthService(), []);
  const syncEngine = useMemo(() => new SyncEngine(), []);

  // Sync core with better error handling and retry logic
  const syncCore = useCallback(
    async (showProgress = false) => {
      if (syncLoading) return;

      try {
        // Start sync process
        setSyncState({ syncLoading: true, syncStatus: 'syncing' });

        // Set initial progress state regardless of showProgress flag
        const initialProgress: SyncProgress = {
          pagesProcessed: 0,
          totalEstimatedPages: 0,
          currentBatch: 0,
          totalBatches: null,
          status: 'collections',
          collectionsProcessed: 0,
          totalEstimatedCollections: 0,
          currentCollectionBatch: 0,
          totalCollectionBatches: null,
          latestProcessedTimestamp: null,
          pagesCompletionPercentage: 0,
          collectionsCompletionPercentage: 0, // Start at 5% to show initial progress
          overallCompletionPercentage: 1,
          currentEntityBeingProcessed: 'Starting sync...',
        };
        if (showProgress) setSyncProgress(initialProgress);

        // Perform sync directly in the main thread
        await syncEngine.performSync({
          // Progress updates
          onProgress: progress => {
            syncLog('Progress update:', progress, { showProgress });
            // Always show progress during sync to ensure UI updates
            if (showProgress) setSyncProgress(progress);

            // Log progress with more detail for debugging
            syncLog(
              `Sync Progress: ${progress.status} - Overall: ${String(progress.overallCompletionPercentage)}%, ` +
                `Collections: ${String(progress.collectionsCompletionPercentage)}%, ` +
                `Pages: ${String(progress.pagesCompletionPercentage)}%, ` +
                `Current: ${progress.currentEntityBeingProcessed || ''}`
            );
          },

          // Collection sync completion - trigger UI update for collections
          onCollectionSyncComplete: () => {
            syncLog('Collections synced, invalidating collection queries');
            void invalidateCollectionQueries(queryClient);
          },
          // Collection batch completion - trigger UI update after each batch
          onCollectionBatchSyncComplete: (batchNumber, totalBatches) => {
            syncLog(
              `Collection batch ${String(batchNumber)} of ${totalBatches ? String(totalBatches) : '?'} synced, invalidating collection queries`
            );
            void invalidateCollectionQueries(queryClient);
          },

          // Page batch completion - trigger UI update after each batch
          onPageBatchSyncComplete: (batchNumber, totalBatches) => {
            syncLog(
              `Page batch ${String(batchNumber)} of ${totalBatches ? String(totalBatches) : '?'} synced, invalidating page queries`
            );
            void invalidatePageQueries(queryClient);
          },

          // Final completion
          onComplete: async (lastSynced, updatedPageIds) => {
            setIsDirty(false);
            await invalidateLocalDataQueries(queryClient);

            // The editor is uncontrolled; remount it when sync changed the open page
            const { currentPageId, updatePageVersion } = usePageStore.getState();
            if (currentPageId && updatedPageIds?.includes(currentPageId)) {
              updatePageVersion();
            }

            syncLog('Sync completed successfully');
            syncLog('Updated Page IDs:', updatedPageIds);

            setSyncState({
              syncLoading: false,
              error: null,
              lastSyncedAt: lastSynced,
              syncStatus: 'success',
            });
          },

          // Offline handler
          onOffline: (error = 'Offline') => {
            setSyncState({
              syncLoading: false,
              error: new Error(error),
              syncStatus: 'error',
            });
            setSyncProgress(null);
          },
        });
      } catch (err) {
        syncError('Sync error:', err);
        setSyncState({
          syncLoading: false,
          error: err instanceof Error ? err : new Error('Sync failed'),
          syncStatus: 'error',
        });
        setSyncProgress(null);
      }
    },
    [syncLoading, syncEngine, setSyncState, setSyncProgress, queryClient, setIsDirty]
  );

  const handleSync = useCallback(() => {
    if (syncLoading || !getCurrentUser()) return;

    const runSync = async () => {
      const lastSynced = await syncRepository.getLastSyncTime();
      const showProgress = !lastSynced;

      syncLog(`Running sync with showProgress: ${showProgress ? 'visible' : 'hidden'}`);
      await syncCore(showProgress);
    };
    void runSync();
  }, [syncLoading, syncCore]);

  const handleSyncRef = useRef(handleSync);
  handleSyncRef.current = handleSync;

  // Auth state management
  useEffect(() => {
    let syncTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = authService.onAuthStateChanged(user => {
      setUser(user);
      setAuthLoading(false);

      if (user?.uid && currentUser?.uid !== user.uid) {
        // Delay sync to avoid race conditions with other processes. Page list seems to load slower if this is running
        syncTimer = setTimeout(() => {
          handleSyncRef.current();
        }, 5000);
      } else {
        void syncRepository.getLastSyncTime().then(lastSyncTime => {
          setLastSyncedAt(lastSyncTime);
        });
      }
    });

    return () => {
      unsubscribe();
      if (syncTimer !== null) clearTimeout(syncTimer);
    };
  }, [authService, currentUser?.uid, setAuthLoading, setLastSyncedAt, setUser]);

  // Public methods

  const signIn = useCallback(async () => {
    try {
      setSyncState({ authLoading: true, error: null });
      await authService.signIn();
    } catch (err) {
      setSyncState({
        error: err instanceof Error ? err : new Error('Sign in failed'),
        authLoading: false,
      });
      throw err;
    }
  }, [authService, setSyncState]);

  const signOut = useCallback(async () => {
    try {
      setSyncState({ error: null });
      await authService.signOut();
      setLastSyncedAt(null);
    } catch (err) {
      setSyncState({
        error: err instanceof Error ? err : new Error('Sign out failed'),
      });
      throw err;
    }
  }, [authService, setSyncState, setLastSyncedAt]);

  const value = useMemo(
    () => ({
      signIn,
      signOut,
      handleSync,
      syncProgress,
    }),
    [signIn, signOut, handleSync, syncProgress]
  );

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

export const useSyncContext = () => {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSyncContext must be used within a SyncProvider');
  }

  // Only return methods, not state
  return context;
};
